import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { apiErrorText } from '../lib/apiErrorText'
import { getSettings, listCampaigns } from './api'
import { firstLine, fmtEastern, STATUSES, statusLabel } from './format'
import { campaignHref, cdHref, titleName } from './nav'
import { formatCents } from './pnl'
import { btnPrimary, focus, inp, lbl, StatusChip } from './ui'


/** Every desk campaign, newest first, with its audience and money at a glance. */
export function ListPage() {
  const [status, setStatus] = useState('')
  const [title, setTitle] = useState('')
  const [params, setParams] = useSearchParams()
  const group = Number(params.get('group')) || null
  const settings = useQuery({ queryKey: ['campaign-desk', 'settings'], queryFn: getSettings, retry: false, staleTime: 30_000 })
  const list = useQuery({
    queryKey: ['campaign-desk', 'list', status, title, group],
    queryFn: () => listCampaigns({ status, title, group }),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  })
  const rows = list.data?.campaigns ?? []
  const agentOn = settings.data?.agent_on

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-[28px] font-medium tracking-tight">Campaigns</h1>
          {agentOn !== undefined && (
            <p className="mt-1 text-[13px] text-paper-dim">
              The agent is <strong className={agentOn ? 'text-radar-green' : 'text-paper'}>{agentOn ? 'on' : 'off'}</strong>.
              {' '}{agentOn ? 'New briefs are drafted and approved campaigns send.' : 'Briefs wait, and nothing sends.'}
              {' '}<Link to={cdHref('settings')} className={`text-amber-glow underline underline-offset-2 ${focus}`}>Settings</Link>
            </p>
          )}
        </div>
        <Link to={cdHref('new')} className={btnPrimary}>New campaign</Link>
      </header>

      <div className="flex flex-wrap gap-4">
        <label className={lbl}>Status
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={inp}>
            <option value="">All</option>
            {STATUSES.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
          </select>
        </label>
        <label className={lbl}>Title
          <select value={title} onChange={(e) => setTitle(e.target.value)} className={inp}>
            <option value="">All titles</option>
            {(settings.data?.titles ?? []).map((t) => <option key={t.slug} value={t.slug}>{t.name}</option>)}
          </select>
        </label>
      </div>

      {group != null && (
        <p className="text-[13px] text-paper-dim">
          Showing the campaigns one every-title brief started. Review and approve each title separately.
          {' '}<button type="button" className={`text-amber-glow underline underline-offset-2 ${focus}`} onClick={() => setParams({})}>Show all campaigns</button>
        </p>
      )}
      {list.error && <p role="alert" className="text-radar-red">{apiErrorText(list.error)}</p>}
      <div className="overflow-x-auto rounded-xl border border-ink-500 bg-ink-700">
        <table className="w-full min-w-[820px] text-left text-[13px]">
          <thead className="border-b border-ink-500 text-[11px] uppercase tracking-[0.1em] text-paper-faint">
            <tr>
              <th className="px-4 py-3 font-bold">Title</th>
              <th className="px-4 py-3 font-bold">Brief</th>
              <th className="px-4 py-3 font-bold">Status</th>
              <th className="px-4 py-3 text-right font-bold">Audience</th>
              <th className="px-4 py-3 text-right font-bold">Expected net</th>
              <th className="px-4 py-3 text-right font-bold">Actual net</th>
              <th className="px-4 py-3 font-bold">Updated</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-ink-500 last:border-0 hover:bg-ink-600">
                <td className="whitespace-nowrap px-4 py-3">{titleName(r.publication_slug)}</td>
                <td className="max-w-[340px] px-4 py-3">
                  <Link to={campaignHref(r.id)} className={`font-semibold text-paper underline-offset-2 hover:underline ${focus}`}>
                    {firstLine(r.brief) || `Campaign #${r.id}`}
                  </Link>
                  {r.group_id != null && <span className="ml-2 whitespace-nowrap rounded bg-ink-600 px-1.5 py-0.5 text-[11px] text-paper-dim">Every title</span>}
                  {r.ignore_limits && <span className="ml-2 whitespace-nowrap rounded bg-amber-wash px-1.5 py-0.5 text-[11px] text-amber-glow">Limits off</span>}
                </td>
                <td className="px-4 py-3"><StatusChip status={r.status} /></td>
                <td className="px-4 py-3 text-right font-mono">{r.audience_count == null ? '—' : r.audience_count.toLocaleString('en-US')}</td>
                <td className="px-4 py-3 text-right font-mono">{r.expected_net_cents == null ? '—' : formatCents(r.expected_net_cents)}</td>
                <td className="px-4 py-3 text-right font-mono">{r.status === 'completed' && r.actual_net_cents != null ? formatCents(r.actual_net_cents) : '—'}</td>
                <td className="whitespace-nowrap px-4 py-3 text-paper-dim">{fmtEastern(r.updated_at)}</td>
              </tr>
            ))}
            {!list.isPending && rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-paper-faint">No campaigns yet. Start one with New campaign.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
